import { useEffect, useRef, useState, useCallback } from "react";

const CHANNEL = "sparkl-classroom-v4";
const STORAGE_KEY = "sparkl-classroom-state-v4";

export const initialSessionState = {
    sessionEnded: false,
    sessionEndedAt: null,
    sessionId: "SESSION_001",
    sessionStarted: false,
    sessionStartedAt: null,
    slideIndex: 0,
    fragmentIndex: 0,
    // Session-only "blank slides with a captured photo" injected after
    // a particular slide. Shape:
    //   { [slideIndex: number]: string[] }  ← list of photo data URLs
    // Example: photoExtras[5] = [url1, url2] adds slides "5.1" and
    // "5.2" between slide 5 and 6 for THIS session only. Restart wipes
    // them (they are NOT part of the lesson JSON).
    photoExtras: {},
    // 0 = on the main slide; 1+ = nth extra photo-slide AFTER the main
    // slide at `slideIndex`. So "5.1" = slideIndex 5, subSlideIndex 1.
    subSlideIndex: 0,
    mode: "intro",
    mascotState: "idle",

    // NEW: has the teacher selected a slide yet this session?
    slideSelected: false,
    // NEW: per-current-slide render mode — 'play' (fragment by fragment) | 'full' (all at once)
    slideRenderMode: "play",
    // NEW: continue auto-advancing slides after one finishes (replaces autoFlowEnabled semantics)
    autoPilotEnabled: false,
    // NEW: a real-life example shown as an overlay popup on student
    examplePopup: null, // exId

    // teaching flow
    currentSection: "concept",
    autoFlowEnabled: true,
    // Start in PAUSED state — the student panel doesn't autoplay
    // narration or fragment progression until the teacher explicitly
    // hits Play on the mobile remote. Prevents the awkward "scanned the
    // QR and the first slide is already talking" experience.
    aiPaused: true,
    blanked: false,
    captionOverride: null,

    // Concept-check / engagement question pushed from the teacher's
    // mobile card to the student smartboard. Shape:
    //   { kind: 'concept-check' | 'engagement', text: '…',
    //     slideId, fragmentIndex }
    // Null means no popup is showing on the smartboard.
    activeQuestion: null,

    // MCQ state
    activeMcqId: null,
    revealMcqAnswer: false,

    // Subjective state
    activeSubjectiveId: null,
    subjectiveMode: null, // 'solved' | 'practice' | null
    revealExpected: false,
    revealCommonMistake: false,
    revealAnswer: false,

    // legacy question (kept for back-compat)
    selectedOption: null,

    // solution
    solutionStep: 0,

    // video
    videoPlaying: false,

    // timer
    timerStartedAt: null,
    timerSeconds: null,
    timerPaused: false,

    activeActivityId: null,
    activityResult: null, // 'reveal' | 'reset' | null
    dragPlacements: {}, // { [itemId]: categoryId }
    dragCheckResult: null, // { correct, total, checks }

    // navigation history
    returnToSlideIdx: null,

    // annotations: { [slideId]: stroke[] } — also used for blank slide
    annotations: {},
    annotationTool: "off",
    annotationColor: "#2E8E9A",

    // blank slide
    blankSlideType: "white",
    aiHandwritingResult: null,

    // AI help
    aiHelpOpen: false,
    aiHelpConcept: null, // key into lesson.teacherHelp
    aiHelpShowToStudents: false,

    // recommendations
    dismissedRecommendations: [], // ids
    activeExampleId: null,

    // command tracking
    lastCommand: null,
    lastCommandAt: null,
    studentHeartbeat: null,
    smartboardActive: 0,
    updatedAt: 0,
};

const load = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return initialSessionState;
        return { ...initialSessionState, ...JSON.parse(raw) };
    } catch {
        return initialSessionState;
    }
};

const save = (state) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
        /* noop */
    }
};

export const useSyncState = () => {
    const [state, setState] = useState(load);
    const channelRef = useRef(null);

    useEffect(() => {
        let bc;
        try {
            bc = new BroadcastChannel(CHANNEL);
            channelRef.current = bc;
            bc.onmessage = (e) => {
                if (e?.data?.type === "state") setState(e.data.payload);
            };
        } catch {
            channelRef.current = null;
        }

        const onStorage = (e) => {
            if (e.key === STORAGE_KEY && e.newValue) {
                try {
                    setState(JSON.parse(e.newValue));
                } catch {
                    /* noop */
                }
            }
        };
        window.addEventListener("storage", onStorage);

        return () => {
            window.removeEventListener("storage", onStorage);
            if (bc) bc.close();
        };
    }, []);

    const set = useCallback((partial) => {
        setState((prev) => {
            const next = {
                ...prev,
                ...(typeof partial === "function" ? partial(prev) : partial),
                updatedAt: Date.now(),
            };
            save(next);
            try {
                channelRef.current?.postMessage({ type: "state", payload: next });
            } catch {
                /* noop */
            }
            return next;
        });
    }, []);

    const sendCommand = useCallback(
        (name, partial = {}) => {
            set((p) => ({
                ...(typeof partial === "function" ? partial(p) : partial),
                lastCommand: name,
                lastCommandAt: Date.now(),
            }));
        },
        [set],
    );

    return [state, set, sendCommand];
};
